import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';

const TimelineView = ({ data, isAdmin, showToast }) => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [activeTrack, setActiveTrack] = useState('ALL'); 
    
    const scrollContainerRef = useRef(null);
    const todayRef = useRef(null);

    const getTrackTheme = (track) => {
        switch(track) {
            case 'BOB': return { hex: '#eab308', text: 'text-yellow-500', bgLight: 'bg-yellow-900/10', border: 'border-yellow-500', shadow: 'shadow-[0_0_30px_rgba(234,179,8,0.15)]', dot: 'bg-yellow-500' };
            case 'OPERATOR': return { hex: '#22c55e', text: 'text-green-500', bgLight: 'bg-green-900/10', border: 'border-green-500', shadow: 'shadow-[0_0_30px_rgba(34,197,94,0.15)]', dot: 'bg-green-500' };
            case 'TAG': return { hex: '#ef4444', text: 'text-red-500', bgLight: 'bg-red-900/10', border: 'border-red-500', shadow: 'shadow-[0_0_30px_rgba(239,68,68,0.15)]', dot: 'bg-red-500' };
            case 'GLOBAL':
            default: return { hex: '#3b82f6', text: 'text-blue-500', bgLight: 'bg-blue-900/10', border: 'border-blue-500', shadow: 'shadow-[0_0_30px_rgba(59,130,246,0.15)]', dot: 'bg-blue-500' };
        }
    };

    const combinedData = useMemo(() => {
        const rawTimeline = data?.timeline || [];
        
        const rawJournals = (data?.journals || []).filter(j => j.date).map(j => {
            let trackName = 'GLOBAL';
            if (j.author === 'BOB') trackName = 'BOB';
            if (j.author === 'DR. MARSH' || j.author === 'CRI' || j.author === 'OPERATOR') trackName = 'OPERATOR';
            if (j.author === 'TAG') trackName = 'TAG';
            return { ...j, type: 'journal', track: trackName, event: j.title, notes: j.text };
        });
        
        const rawLore = (data?.ideas || []).filter(i => i.date).map(i => ({ ...i, type: 'lore', track: 'GLOBAL', event: i.title || i.name, notes: i.lore || i.desc }));
        const rawArtifacts = (data?.artifacts || []).filter(a => a.date).map(a => ({ ...a, type: 'artifact', track: 'GLOBAL', event: a.title || a.name, notes: a.desc }));
        
        return [...rawTimeline, ...rawJournals, ...rawLore, ...rawArtifacts].map(e => ({
            ...e,
            track: e.track === 'DR. MARSH' ? 'OPERATOR' : e.track
        }));
    }, [data]);

    const filteredData = useMemo(() => {
        if (activeTrack === 'ALL') return combinedData;
        return combinedData.filter(e => e.track === activeTrack);
    }, [combinedData, activeTrack]);

    const floatingEvents = useMemo(() => filteredData.filter(e => !e.date || e.date === 'TBD'), [filteredData]);
    
    const sortedData = useMemo(() => {
        return filteredData
            .filter(e => e.date && e.date !== 'TBD')
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [filteredData]);

    useEffect(() => {
        if (todayRef.current && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const target = todayRef.current;
            container.scrollLeft = target.offsetLeft - (container.clientWidth / 2) + 60;
        }
    }, [activeTrack, sortedData.length]); 

    const handleSaveEvent = async (formData) => { 
        const payload = { ...formData, date: formData.date || 'TBD', track: formData.track || 'GLOBAL' };
        if(payload.isAnomaly === undefined) payload.isAnomaly = false;

        try { 
            if (editingEvent) {
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'timeline', editingEvent.id), payload);
                showToast("Event updated.", "success");
                if (selectedEvent?.id === editingEvent.id) setSelectedEvent({ ...editingEvent, ...payload });
            } else {
                await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'timeline'), { ...payload, timestamp: serverTimestamp() }); 
                showToast("Event logged successfully.", "success");
            }
            setShowAddModal(false);
            setEditingEvent(null);
        } 
        catch (err) { showToast("Firebase Error.", "error"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Purge this event? Note: If this is an Artifact or Journal, it will be deleted from the database entirely.")) return;
        
        let colName = 'timeline';
        const itemToDelete = combinedData.find(d => d.id === id);
        if (itemToDelete) {
            if (itemToDelete.type === 'journal') colName = 'journals';
            else if (itemToDelete.type === 'lore') colName = 'ideas';
            else if (itemToDelete.type === 'artifact') colName = 'artifacts';
        }

        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', colName, id));
            showToast("Event purged.", "success");
            if (selectedEvent?.id === id) setSelectedEvent(null);
        } catch(e) { showToast("Error.", "error"); }
    };

    const openEditModal = (event) => {
        setEditingEvent(event);
        setShowAddModal(true);
    };

    const todayStr = new Date().toISOString().split('T')[0];
    const tracks = ['ALL', 'GLOBAL', 'BOB', 'OPERATOR', 'TAG'];

    const activeTheme = getTrackTheme(activeTrack);

    return (
        <div className="flex flex-col h-full overflow-hidden relative bg-[#050505] fade-in">
            
            {/* TOP HEADER CONTROLS */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center p-4 border-b border-gray-900 bg-black/90 z-20 gap-4 shrink-0 shadow-lg">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-white font-sans tracking-tight">
                        <Icons.Activity className={activeTrack === 'ALL' ? 'text-blue-600' : activeTheme.text} /> 
                        <span className="hidden sm:inline">NARRATIVE</span> TIMELINE
                    </h2>
                    {activeTrack !== 'ALL' && activeTrack !== 'GLOBAL' && (
                        <button 
                            onClick={() => setActiveTrack('GLOBAL')} 
                            style={{ color: activeTheme.hex, borderColor: activeTheme.hex }}
                            className="bg-black hover:bg-gray-900 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border"
                        >
                            <Icons.ArrowLeft size={12} /> Return to Prime
                        </button>
                    )}
                </div>
                
                <div className="flex items-center justify-between w-full md:w-auto gap-4">
                    <div className="flex bg-gray-900/50 p-1 rounded border border-gray-800 overflow-x-auto custom-scrollbar">
                        {tracks.map(t => {
                            const btnTheme = getTrackTheme(t);
                            return (
                                <button 
                                    key={t} 
                                    onClick={() => setActiveTrack(t)}
                                    style={activeTrack === t ? { backgroundColor: btnTheme.hex, color: '#000' } : {}}
                                    className={`px-3 py-1 text-[9px] font-bold tracking-widest uppercase rounded transition-colors whitespace-nowrap ${activeTrack !== t ? 'text-gray-500 hover:text-white' : ''}`}
                                >
                                    {t}
                                </button>
                            );
                        })}
                    </div>

                    {isAdmin && (
                        <button onClick={() => { setEditingEvent(null); setShowAddModal(true); }} className="bg-blue-900/20 text-blue-400 border border-blue-900/50 px-3 py-1.5 rounded text-xs hover:bg-blue-900/40 flex items-center gap-2 font-bold tracking-wider shrink-0">
                            <Icons.Plus size={14} /> <span className="hidden sm:inline">INJECT</span>
                        </button>
                    )}
                </div>
            </div>
            
            {/* FLOATING UNANCHORED EVENTS */}
            {floatingEvents.length > 0 && (
                <div className="bg-[#0a0a0a] border-b border-gray-900 p-4 shrink-0 flex gap-4 overflow-x-auto custom-scrollbar items-center z-10 shadow-lg">
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest shrink-0 self-center pr-4 border-r border-gray-800">Unanchored<br/>Threads</div>
                    {floatingEvents.map(item => {
                        const isAnomaly = item.isAnomaly;
                        const itemTheme = getTrackTheme(item.track || 'GLOBAL');
                        const isSelected = selectedEvent?.id === item.id;
                        
                        const borderColor = isSelected 
                            ? (isAnomaly ? 'border-purple-500' : `border-[${itemTheme.hex}]`) 
                            : (isAnomaly ? 'border-purple-900/50 hover:border-purple-500' : 'border-gray-700 hover:border-gray-400');
                        
                        const bgColor = isSelected ? (isAnomaly ? 'bg-purple-900/10' : itemTheme.bgLight) : 'bg-black';

                        return (
                            <div key={item.id} onClick={() => setSelectedEvent(item)} className={`${bgColor} border border-dashed p-3 rounded cursor-pointer shrink-0 min-w-[160px] transition-colors ${borderColor}`} style={isSelected && !isAnomaly ? { borderColor: itemTheme.hex } : {}}>
                                <div className="flex gap-2 items-center mb-1">
                                    <span className="text-[8px] bg-gray-800 text-gray-400 px-1 rounded">{item.track || 'GLOBAL'}</span>
                                    {isAnomaly && <span className="text-[8px] text-purple-400 font-bold flex items-center gap-1 animate-pulse"><Icons.DoorOpen size={10} /> ANOMALY</span>}
                                </div>
                                <h4 className="text-xs font-bold text-white truncate pr-4">{item.title || item.event}</h4>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MAIN TIMELINE CANVAS */}
            <div ref={scrollContainerRef} className="flex-1 relative overflow-x-auto overflow-y-hidden bg-black/20 custom-scrollbar scroll-smooth">
                {sortedData.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-gray-700 font-mono text-sm uppercase tracking-widest">TIMELINE EMPTY.</div>}
                
                <div className="relative h-full flex items-center min-w-max px-24 py-12">
                    {/* The Main Horizontal Axis */}
                    <div className="absolute left-0 right-0 top-1/2 h-px -z-0 opacity-50" style={{ backgroundColor: activeTrack === 'ALL' ? '#374151' : activeTheme.hex }}></div>

                    {(() => {
                        let todayInjected = false;
                        let stepCount = 0; 

                        return sortedData.map((item, index) => {
                            const isPast = item.date < todayStr;
                            const isSelected = selectedEvent?.id === item.id;
                            const isAnomaly = item.isAnomaly;
                            const itemTheme = getTrackTheme(item.track || 'GLOBAL');
                            
                            const currentStep = stepCount % 4;
                            const isTopSide = currentStep === 0 || currentStep === 2;
                            const distance = (currentStep === 0 || currentStep === 3) ? 190 : 70;
                            const lineHeight = isTopSide ? -distance : distance;

                            const cardPositionStyle = isTopSide 
                                ? { bottom: `calc(50% + ${distance + 15}px)` } 
                                : { top: `calc(50% + ${distance + 15}px)` };

                            stepCount++;

                            const strokeColor = isSelected ? itemTheme.hex : (isAnomaly ? '#a855f7' : '#374151');
                            const strokeDash = isAnomaly ? '4 4' : (item.type === 'esoteric' ? '4 4' : '0');
                            
                            const cardBorderClass = isAnomaly ? (isSelected ? 'border-purple-500 shadow-[0_0_30px_rgba(168,85,247,0.15)] z-50 scale-105' : 'border-purple-900/50 hover:border-purple-500 z-10') : (isSelected ? 'z-50 scale-105' : 'border-gray-800 hover:border-gray-500 z-10');

                            let todayMarker = null;
                            if (!todayInjected && item.date >= todayStr) {
                                todayInjected = true;
                                todayMarker = (
                                    <div ref={todayRef} key="today" className="relative flex flex-col items-center mx-8 h-full justify-center" style={{ width: '40px' }}>
                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 w-[2px] h-32 bg-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.8)]"></div>
                                        <div className="absolute top-1/2 mt-16 text-[10px] font-black text-cyan-400 bg-black px-2 py-1 border border-cyan-900 rounded shadow-lg uppercase tracking-widest">TODAY</div>
                                    </div>
                                );
                            }

                            const EventNode = (
                                <div key={item.id} className={`relative flex flex-col items-center mx-6 group h-full justify-center transition-opacity duration-500 ${isPast ? 'opacity-50 hover:opacity-100' : 'opacity-100'}`} style={{ width: '140px' }} onClick={() => setSelectedEvent(item)}>
                                    
                                    <svg className="absolute overflow-visible pointer-events-none z-0" style={{ top: '50%', left: '50%', width: '1px', height: '1px' }}>
                                        <line x1="0" y1="0" x2="0" y2={lineHeight} stroke={strokeColor} strokeWidth={isSelected ? 2 : 1} strokeDasharray={strokeDash} className="transition-all duration-300"/>
                                    </svg>
                                    
                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-[#050505] p-1 flex items-center justify-center">
                                        {isAnomaly ? (
                                            <div className={`p-1.5 rounded-full border-2 transition-all duration-300 ${isSelected ? 'bg-purple-900 border-white scale-125 shadow-[0_0_15px_rgba(168,85,247,0.8)]' : 'bg-purple-900/50 border-purple-500 animate-pulse group-hover:bg-purple-500 group-hover:border-white'}`}>
                                                <Icons.DoorOpen size={16} className="text-white" />
                                            </div>
                                        ) : (
                                            <div 
                                                className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${isSelected ? 'border-white scale-150' : 'border-gray-600'}`}
                                                style={{ backgroundColor: isSelected ? itemTheme.hex : '#1f2937' }}
                                            ></div>
                                        )}
                                    </div>
                                    
                                    <div 
                                        className={`absolute text-[9px] font-mono whitespace-nowrap tracking-widest ${!isSelected ? 'text-gray-600 group-hover:text-gray-400' : ''}`}
                                        style={{ 
                                            ...(isSelected ? { color: isAnomaly ? '#a855f7' : itemTheme.hex } : {}),
                                            ...(isTopSide ? { top: isAnomaly ? 'calc(50% + 24px)' : 'calc(50% + 15px)' } : { bottom: isAnomaly ? 'calc(50% + 24px)' : 'calc(50% + 15px)' })
                                        }}
                                    >
                                        {item.date}
                                    </div>
                                    
                                    <div 
                                        className={`absolute w-56 p-4 rounded bg-[#0a0a0a] transition-all duration-300 cursor-pointer border ${cardBorderClass}`} 
                                        style={Object.assign({}, cardPositionStyle, isSelected && !isAnomaly ? { borderColor: itemTheme.hex, boxShadow: `0 0 30px ${itemTheme.hex}40` } : {})}
                                    >
                                        <div className="flex justify-between items-start mb-2 gap-2">
                                            <div className="flex gap-1 flex-wrap">
                                                <span className={`text-[8px] uppercase font-bold px-1.5 py-0.5 rounded tracking-wider ${item.type === 'critical' ? 'bg-red-900/30 text-red-400 border border-red-900/50' : 'bg-gray-900 text-gray-400 border border-gray-800'}`}>
                                                    {item.type || 'LOG'}
                                                </span>
                                            </div>
                                            <span className="text-[8px] uppercase font-bold" style={{ color: itemTheme.hex }}>{item.track || 'GLOBAL'}</span>
                                        </div>
                                        <h3 className="font-bold text-gray-100 text-sm leading-snug font-sans">{item.title || item.event}</h3>
                                        {item.linkedItem && (
                                            <div className="mt-2 text-[9px] text-blue-400 flex items-center gap-1 font-bold uppercase tracking-widest bg-blue-900/20 w-fit px-1.5 py-0.5 rounded border border-blue-900/50">
                                                <Icons.ExternalLink size={8} /> {item.linkedItem}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );

                            return todayMarker ? [todayMarker, EventNode] : EventNode;
                        });
                    })()}

                    {(() => {
                        if (sortedData.length > 0 && sortedData[sortedData.length - 1].date < todayStr) {
                            return (
                                <div ref={todayRef} className="relative flex flex-col items-center mx-16 h-full justify-center" style={{ width: '40px' }}>
                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 w-[2px] h-32 bg-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.8)]"></div>
                                    <div className="absolute top-1/2 mt-16 text-[10px] font-black text-cyan-400 bg-black px-2 py-1 border border-cyan-900 rounded shadow-lg uppercase tracking-widest">TODAY</div>
                                </div>
                            );
                        }
                        return null;
                    })()}

                </div>
            </div>

            {/* CLICK-TO-CLOSE OVERLAY FOR ENTIRE SCREEN */}
            {selectedEvent && (
                <div 
                    className="fixed inset-0 bg-transparent z-[90]" 
                    onClick={() => setSelectedEvent(null)}
                ></div>
            )}

            {/* RIGHT DRAWER */}
            <div className={`fixed inset-y-0 right-0 w-full md:w-[400px] bg-[#0a0a0a] border-l border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out z-[100] flex flex-col ${selectedEvent ? 'translate-x-0' : 'translate-x-full'}`}>
                {selectedEvent && (
                    <>
                        <div className="p-6 border-b border-gray-900 bg-black flex justify-between items-start shrink-0">
                            <div>
                                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    {selectedEvent.date} <span className="text-gray-700">|</span> <span style={{ color: getTrackTheme(selectedEvent.track || 'GLOBAL').hex }}>{selectedEvent.track || 'GLOBAL'}</span>
                                </div>
                                <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight">{selectedEvent.title || selectedEvent.event}</h2>
                            </div>
                            
                            {/* Sticky Header Close Button */}
                            <button onClick={() => setSelectedEvent(null)} className="text-gray-400 hover:text-white p-3 transition-colors bg-gray-900 rounded flex items-center justify-center shrink-0">
                                <Icons.X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-6">
                            
                            {/* THE DOOR / FORK MECHANIC */}
                            {selectedEvent.isAnomaly && (
                                <div className="bg-black border border-purple-500/50 p-5 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Icons.Activity className="text-purple-400 shrink-0 animate-pulse" size={24} />
                                        <div>
                                            <div className="text-[10px] font-black text-purple-400 uppercase tracking-widest">TEMPORAL ANOMALY DETECTED</div>
                                            <div className="text-xs text-gray-400 font-mono mt-0.5">This event spawned a timeline fork.</div>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            setActiveTrack(selectedEvent.track === 'GLOBAL' ? 'BOB' : selectedEvent.track); 
                                            setSelectedEvent(null);
                                        }} 
                                        className="w-full mt-4 py-4 bg-purple-900/40 border border-purple-500 hover:bg-purple-600 text-white font-black text-lg md:text-sm uppercase tracking-widest rounded shadow-[0_0_20px_rgba(168,85,247,0.4)] flex items-center justify-center gap-3 transition-all transform hover:scale-105"
                                    >
                                        <Icons.DoorOpen size={20} /> ENTER BRANCHED TIMELINE
                                    </button>
                                </div>
                            )}

                            <div className="flex gap-2 flex-wrap">
                                <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded tracking-wider border ${selectedEvent.type === 'critical' ? 'bg-red-900/20 text-red-400 border-red-900/50' : 'bg-gray-900 text-gray-400 border-gray-800'}`}>
                                    TYPE: {selectedEvent.type || 'LOG'}
                                </span>
                                {selectedEvent.location && (
                                    <span className="text-[10px] uppercase font-bold px-2 py-1 rounded tracking-wider bg-cyan-900/10 text-cyan-500 border border-cyan-900/30 flex items-center gap-1">
                                        <Icons.MapPin size={10} /> {selectedEvent.location}
                                    </span>
                                )}
                            </div>

                            {selectedEvent.linkedItem && (
                                <div className="bg-blue-900/10 border border-blue-900/30 p-4 rounded">
                                    <div className="text-[9px] font-bold uppercase tracking-widest text-blue-500 mb-1 flex items-center gap-1"><Icons.ExternalLink size={10}/> Linked Asset</div>
                                    <div className="text-sm font-mono text-blue-200">{selectedEvent.linkedItem}</div>
                                </div>
                            )}

                            <div>
                                <h4 className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-3 border-b border-gray-900 pb-2">Operational Notes</h4>
                                <p className="text-sm text-gray-300 font-sans leading-relaxed whitespace-pre-wrap">{selectedEvent.notes}</p>
                            </div>
                        </div>

                        {isAdmin && (
                            <div className="p-4 border-t border-gray-900 bg-black flex flex-col sm:flex-row gap-2 shrink-0 pb-safe">
                                <button onClick={() => handleDelete(selectedEvent.id)} className="p-4 md:p-3 text-gray-600 hover:text-red-500 hover:bg-red-900/20 transition-colors border border-gray-800 rounded flex items-center justify-center">
                                    <Icons.Trash2 size={16} />
                                </button>
                                {selectedEvent.type !== 'artifact' && selectedEvent.type !== 'lore' && selectedEvent.type !== 'journal' && (
                                    <button onClick={() => openEditModal(selectedEvent)} className="flex-1 bg-blue-900/40 border border-blue-800 hover:bg-blue-600 text-white p-4 md:p-3 text-xs font-bold uppercase tracking-widest rounded transition-colors flex items-center justify-center gap-2">
                                        <Icons.Edit2 size={14} /> EDIT RECORD
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {(showAddModal || editingEvent) && (
                <AddDataModal 
                    type="timeline_event" 
                    initialData={editingEvent || { track: activeTrack === 'ALL' ? 'GLOBAL' : activeTrack }} 
                    onClose={() => { setShowAddModal(false); setEditingEvent(null); }} 
                    onSubmit={handleSaveEvent} 
                    showToast={showToast} 
                    isAdmin={isAdmin}
                />
            )}
        </div>
    );
};

export default TimelineView;